// Connection-status overlays (#242). Tells the user what's happening while the
// app is connecting and when the Shiny session drops.
//
// The overlay markup is server-rendered in app/main.R; this module only flips
// two classes on <body> and CSS (app/styles/_connection.scss) does the showing
// and hiding:
//
//   (no class)           -> "Starting the simulator…" splash visible
//   .beamm-connected     -> splash hidden, app interactive
//   .beamm-disconnected  -> "Your session has ended" panel visible
//
// No React: once the websocket is dead the island state is moot, so a plain
// DOM/CSS overlay driven by the lifecycle events is the robust choice. Mirrors
// the vanilla-module + idempotent-init shape of app/js/nav/jump_buttons.ts.
//
// We bind through jQuery, not document.addEventListener. Shiny fires its
// lifecycle events with `$(document).trigger(...)`, which invokes jQuery
// handlers only — native addEventListener never sees them (confirmed: a native
// listener stayed silent through connect while a jQuery one fired). jQuery
// registers via addEventListener under the hood, so a jQuery `.on()` handler
// still catches a native dispatchEvent too (which is how the Cypress tests
// drive it). jQuery ships with Shiny, so it is always present.
//
// shiny:disconnected fires on both an inactivity stop and a crash; both surface
// as a dropped socket, so one panel covers both. We do not call
// session$allowReconnect: a container stopped for inactivity is gone, so the
// honest move is a Reload button, not a spinner that can't succeed.

const JQUERY_RETRY_MS = 50;
const JQUERY_MAX_RETRIES = 60; // ~3s; jQuery is a Shiny head dependency

let initialised = false;

export function initConnectionOverlays(): void {
  if (initialised) return;
  initialised = true;

  const markConnected = (): void => {
    document.body.classList.add("beamm-connected");
    document.body.classList.remove("beamm-disconnected");
  };

  const markDisconnected = (): void => {
    document.body.classList.add("beamm-disconnected");
  };

  bindWhenJQueryReady((jq) => {
    jq(document).on("shiny:connected shiny:sessioninitialized", markConnected);
    jq(document).on("shiny:disconnected", markDisconnected);
    // The bundle can finish parsing after Shiny has already connected; in that
    // case the connect event fired before we bound, so reconcile against
    // Shiny's own state so the splash doesn't stick.
    if (window.Shiny?.shinyapp?.isConnected?.()) markConnected();
  });

  // The reload button is plain DOM; wire it once <body> exists.
  const wireReload = (): void => {
    document
      .getElementById("beamm-reload-app")
      ?.addEventListener("click", () => window.location.reload());
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", wireReload);
  } else {
    wireReload();
  }
}

// jQuery may not be evaluated yet when this module runs from <head>; poll
// briefly for it, the same shape app/js/nav/jump_buttons.ts uses to wait for
// shiny.router's DOM.
function bindWhenJQueryReady(bind: (jq: NonNullable<Window["jQuery"]>) => void): void {
  let attempts = 0;
  const tryBind = (): void => {
    const jq = window.jQuery;
    if (jq) {
      bind(jq);
      return;
    }
    if (++attempts < JQUERY_MAX_RETRIES) {
      window.setTimeout(tryBind, JQUERY_RETRY_MS);
    }
  };
  tryBind();
}
